import { Link } from "react-router-dom";
import { Command, ArrowLeft } from "lucide-react";

const BRAND = "#0B1F33";

export function LegalLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#F4F5EF] text-[#111111]">
      <header className="border-b border-[#CDD7E3] bg-white/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: BRAND }}>
              <Command className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-[14px] font-semibold">RentaScan</span>
          </Link>
          <Link to="/" className="text-[12.5px] text-[#5E6875] hover:text-[#111111] inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à l'accueil
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="text-[26px] font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-[#5E6875]">{subtitle}</p>}
        <div className="mt-6 space-y-5 text-[14px] leading-relaxed text-[#111111]">
          {children}
        </div>
      </main>
    </div>
  );
}

export function Mentions() {
  return (
    <LegalLayout title="Mentions légales" subtitle="Dernière mise à jour : février 2026" >
      <Section title="Éditeur">
        <p>Le service <strong>RentaScan</strong> est édité par un éditeur en cours de constitution. Les informations ci-dessous seront complétées à la finalisation de la structure juridique.</p>
        <Field label="Raison sociale" value="À compléter" />
        <Field label="Forme juridique" value="À compléter" />
        <Field label="SIRET / RCS" value="À compléter" />
        <Field label="Capital social" value="À compléter" />
        <Field label="Siège social" value="À compléter" />
        <Field label="Directeur de la publication" value="À compléter" />
        <Field label="Contact" value="À compléter (adresse e-mail de contact)" />
      </Section>
      <Section title="Hébergeur">
        <Field label="Prestataire" value="Emergent Cloud" />
        <Field label="Infrastructure" value="Environnement conteneurisé Kubernetes multi-régions" />
      </Section>
      <Section title="Propriété intellectuelle">
        <p>L'ensemble des éléments composant le service (textes, interfaces, logos, code source, base de données) est protégé par le Code de la propriété intellectuelle. Toute reproduction ou représentation non autorisée est interdite.</p>
      </Section>
      <Section title="Responsabilité">
        <p>Les analyses et projections fournies par RentaScan sont des <strong>estimations</strong> basées sur les factures et informations saisies. Elles ne constituent pas un avis comptable ou fiscal. Chaque restaurateur reste seul décisionnaire de ses choix commerciaux.</p>
      </Section>
    </LegalLayout>
  );
}

export function Confidentialite() {
  return (
    <LegalLayout title="Politique de confidentialité" subtitle="Dernière mise à jour : février 2026">
      <Section title="Données collectées">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Compte</strong> : prénom, nom, adresse e-mail, nom d'établissement, mot de passe (haché avec bcrypt).</li>
          <li><strong>Usage</strong> : factures scannées, produits, fournisseurs, recettes, historique de prix, préférences de menu.</li>
          <li><strong>Technique</strong> : cookies d'authentification (session), logs d'accès agrégés.</li>
        </ul>
      </Section>
      <Section title="Finalités">
        <ul className="list-disc pl-5 space-y-1">
          <li>Fournir le service (extraction, calculs, publication du menu).</li>
          <li>Assurer la sécurité (anti-brute force, gestion de session).</li>
          <li>Améliorer le service (statistiques agrégées, jamais nominatives).</li>
        </ul>
      </Section>
      <Section title="Sous-traitants">
        <p>Les sous-traitants suivants interviennent :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Emergent Cloud</strong> — hébergement.</li>
          <li><strong>Emergent Integrations</strong> — envoi d'e-mails transactionnels (réinitialisation de mot de passe, résumé hebdo).</li>
          <li><strong>OpenAI / Anthropic / Google</strong> — analyse RentaScan des factures et rédaction des messages fournisseurs (via clé Emergent Universelle). Les données envoyées ne sont pas utilisées pour l'entraînement.</li>
        </ul>
      </Section>
      <Section title="Durée de conservation">
        <p>Vos données sont conservées tant que votre compte est actif. Un compte supprimé entraîne l'effacement complet sous 30 jours (à l'exception des factures comptables conservées 10 ans conformément à la réglementation française).</p>
      </Section>
      <Section title="Vos droits">
        <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'opposition, de portabilité et d'effacement. Pour l'exercer, contactez-nous à l'adresse à compléter dans les mentions légales.</p>
      </Section>
      <Section title="Cookies">
        <p>Nous utilisons uniquement des cookies techniques strictement nécessaires (session authentifiée). Aucun cookie publicitaire ni traceur tiers.</p>
      </Section>
    </LegalLayout>
  );
}

export function CGU() {
  return (
    <LegalLayout title="Conditions générales d'utilisation" subtitle="Dernière mise à jour : février 2026">
      <Section title="1. Objet">
        <p>Les présentes conditions régissent l'utilisation du service <strong>RentaScan</strong>, plateforme de suivi des prix d'achat, de calcul de rentabilité et d'édition de menu pour restaurateurs indépendants.</p>
      </Section>
      <Section title="2. Compte utilisateur">
        <p>L'utilisateur s'engage à fournir des informations exactes lors de son inscription. Il est responsable de la confidentialité de son mot de passe. Toute action réalisée depuis son compte est réputée effectuée par lui.</p>
      </Section>
      <Section title="3. Période d'essai et abonnement">
        <p>Une période d'essai gratuite de <strong>14 jours</strong> est offerte à l'inscription, sans carte bancaire requise. À l'issue, l'utilisateur peut souscrire :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Formule Mensuelle</strong> : 49 € HT / mois / établissement.</li>
          <li><strong>Formule Annuelle</strong> : 490 € HT / an / établissement (deux mois offerts).</li>
        </ul>
        <p>L'abonnement est reconduit tacitement. L'utilisateur peut le résilier à tout moment ; la résiliation prend effet à la fin de la période en cours.</p>
      </Section>
      <Section title="4. Utilisation du service">
        <p>L'utilisateur s'engage à ne pas :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Utiliser le service à des fins illégales.</li>
          <li>Charger de factures ne lui appartenant pas.</li>
          <li>Tenter de contourner les mécanismes de sécurité.</li>
          <li>Extraire ou réutiliser massivement les données du service.</li>
        </ul>
      </Section>
      <Section title="5. Nature des analyses">
        <p>Les <strong>estimations, projections et gains potentiels</strong> présentés par le service sont des indications à visée décisionnelle. Elles ne constituent en aucun cas un engagement de résultat ni un conseil comptable, fiscal ou juridique. Le restaurateur reste seul décisionnaire.</p>
      </Section>
      <Section title="6. Responsabilité">
        <p>L'éditeur met en œuvre ses meilleurs efforts pour garantir la disponibilité et la fiabilité du service. Sa responsabilité ne saurait être engagée en cas de dommage indirect (perte d'exploitation, perte de chance, préjudice commercial).</p>
      </Section>
      <Section title="7. Droit applicable">
        <p>Les présentes conditions sont régies par le droit français. Tout litige sera soumis aux tribunaux compétents du siège de l'éditeur, sauf application impérative d'une autre juridiction.</p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-[16px] font-semibold text-[#111111] mb-2">{title}</h2>
      <div className="space-y-2 text-[#111111]/90">{children}</div>
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-[13.5px]">
      <span className="text-[#5E6875] w-40 shrink-0">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
